/* eslint-disable @typescript-eslint/naming-convention */
import {inject, Injectable, signal} from '@angular/core';
import {CartApiService} from './data/api/service';
import {select, Store} from '@ngrx/store';
import * as ContextDataActions from './common/context-data/context-data.actions';
import * as CurrentUserActions from './common/current-user/current-user.actions';
import * as CartActions from './common/shopping-cart/cart.actions';
import * as AddressActions from './tax-and-shipping/address.actions';
import * as AddonActions from './add-ons/add-on.actions';
import {selectContextData} from './common/context-data/context-data.selectors';
import {filter, timeout} from 'rxjs/operators';
import {firstValueFrom, throwError} from 'rxjs';
import {CommerceEventEmitterService} from './common/event-emitter/commerce-event-emitter.service';
import {CartCompanionEventEmitterService} from './common/event-emitter/cart-companion-event-emitter.service';
import {
  CommerceEventEmitter,
  CommerceNavigationEventEmitter,
  isCommerceNavigationEventEmitter,
} from './common/event-emitter/commerce-event-emitter';
import {Actions, ofType} from '@ngrx/effects';

@Injectable({providedIn: 'root'})
export class CommerceInitializationService {
  initialized = signal(false);
  private initializationPromise?: Promise<void>;

  //#region - services -
  private store: Store;
  private cartApi: CartApiService;
  private commerceEventEmitter: CommerceEventEmitterService;
  private cartCompanionEventEmitter: CartCompanionEventEmitterService;
  private actions$: Actions;
  //#endregion - services -

  constructor() {
    this.store = inject(Store);
    this.cartApi = inject(CartApiService);
    this.commerceEventEmitter = inject(CommerceEventEmitterService);
    this.cartCompanionEventEmitter = inject(CartCompanionEventEmitterService);
    this.actions$ = inject(Actions);
  }

  set context(value: string) {
    this.store.dispatch(
      ContextDataActions.loadContextData({contextJson: value}),
    );
  }

  set enableCartSlideOut(value: boolean) {
    this.store.dispatch(CartActions.setEnableCartSlideOut({enabled: value}));
  }

  set enableAddressCollection(value: boolean) {
    this.store.dispatch(
      AddressActions.setEnableAddressCollection({enabled: value}),
    );
  }

  async initialize(
    entryComponent: CommerceNavigationEventEmitter | CommerceEventEmitter,
  ): Promise<void> {
    if (isCommerceNavigationEventEmitter(entryComponent)) {
      this.commerceEventEmitter.bind(entryComponent);
    } else {
      this.cartCompanionEventEmitter.bind(entryComponent);
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initialized.set(false);
    this.initializationPromise = this.runInitialization().finally(() => {
      this.initializationPromise = undefined;
    });

    return this.initializationPromise;
  }

  async runInitialization(): Promise<void> {
    this.store.dispatch(AddonActions.initializationStart());

    await this.waitForContextDataLoaded();
    await this.initializeClareCart();
    await this.initializeCurrentUser();

    this.store.dispatch(CartActions.loadCart());
    await this.waitForCartToLoad();

    this.store.dispatch(AddonActions.initializationComplete());
    this.initialized.set(true);
  }

  async waitForCartToLoad(): Promise<void> {
    await firstValueFrom(
      this.actions$.pipe(
        ofType(CartActions.loadCartSuccess, CartActions.loadCartFailure),
        timeout({
          each: 10000,
          with: () => {
            throw new Error(
              'Initialization timed out waiting for cart to load',
            );
          },
        }),
      ),
    );
  }

  async initializeClareCart(): Promise<void> {
    const context = this.store.selectSignal(selectContextData);
    if (context()?.guestKey) {
      return;
    }

    const cart = await firstValueFrom(this.cartApi.initializeClareCart());
    this.store.dispatch(
      ContextDataActions.updateContextData({
        context: {guestKey: cart.guest_key},
      }),
    );
  }

  async initializeCurrentUser(): Promise<void> {
    this.store.dispatch(CurrentUserActions.loadCurrentUser());

    await firstValueFrom(
      this.actions$.pipe(
        ofType(
          CurrentUserActions.loadCurrentUserSuccess,
          CurrentUserActions.loadCurrentUserFailure,
        ),
        timeout({
          each: 10000,
          with: () =>
            throwError(
              () =>
                new Error(
                  'Initialization timed out waiting for current user to load',
                ),
            ),
        }),
      ),
    );
  }

  async waitForContextDataLoaded(): Promise<void> {
    await firstValueFrom(
      this.store.pipe(
        select(selectContextData),
        filter(contextData => !!contextData),
        timeout({
          each: 200,
          with: () => {
            throw new Error(
              'Initialization timed out waiting for context data to load',
            );
          },
        }),
      ),
    );
  }
}
